import { Download, FileIcon, FileText, Pause, Play } from 'lucide-react';
import { FC, ReactNode, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useWorkspace } from '@/features/workspaces/hooks';
import { BaseModal } from '@/lib/Modals';
import { SpinnerIcon } from '@/lib/Spinner';
import { axiosClient } from '@/services/axiosClient';
import type { WhatsappMessage } from '@/services/endpoints/whatsappInbox';
import { mediaKind, mediaUrl } from '../inboxUtils';
import { useAuthenticatedObjectUrl } from '../useAuthenticatedObjectUrl';

const NOTE_CLASS = 'text-sm italic opacity-70';
// Some browsers read the blob after click() returns: revoke a moment later, not on the spot.
const REVOKE_DELAY_MS = 1_000;

/** Media sits inside the bubble: tinted white on the brand-coloured outbound one, grey on the white inbound one. */
const cardClass = (outbound: boolean) =>
  twMerge(
    'flex w-64 max-w-full items-center gap-3 rounded-xl p-2 text-left disabled:cursor-wait',
    outbound ? 'bg-white-16 hover:bg-white-25' : 'bg-primary-background hover:bg-gray-100'
  );

const extension = (name: string) => (name.includes('.') ? (name.split('.').pop() ?? '').toUpperCase() : '');

const CardText: FC<{ name: string; detail: string }> = ({ name, detail }) => (
  <span className='min-w-0 flex-1'>
    <span className='block truncate font-medium'>{name}</span>
    {detail ? <span className='block text-xs opacity-70'>{detail}</span> : null}
  </span>
);

const PreviewModal: FC<{ title: string; onClose: VoidFunction; children: ReactNode }> = ({ title, onClose, children }) => {
  const titleId = useId();
  return (
    <BaseModal onClose={onClose} aria-labelledby={titleId} classNames={{ dialog: 'max-w-[95vw]', content: 'min-h-0 min-w-0 gap-3 p-4' }}>
      <p id={titleId} className='truncate pr-10 text-sm font-medium text-primary-900'>
        {title}
      </p>
      {children}
    </BaseModal>
  );
};

const DownloadLink: FC<{ url: string; name: string }> = ({ url, name }) => {
  const { t } = useTranslation('whatsappInbox');
  return (
    <a href={url} download={name} className='flex w-fit items-center gap-1 text-sm font-medium text-brand-600 underline'>
      <Download aria-hidden className='size-4' />
      {t('download')} {name}
    </a>
  );
};

/** Download-only documents can weigh up to 100 MB: they are fetched when the operator asks, never on opening the chat. */
const FileDownload: FC<{ message: WhatsappMessage; name: string }> = ({ message, name }) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');

  const download = async () => {
    setState('loading');
    try {
      const response = await axiosClient.get(mediaUrl(workspace.id, message.id), { responseType: 'blob', timeout: 60_000 });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
      setState('idle');
    } catch {
      setState('failed');
    }
  };

  if (state === 'failed') return <p className={NOTE_CLASS}>{t('mediaUnavailable')}</p>;
  return (
    <button
      type='button'
      aria-label={`${t('download')} ${name}`}
      disabled={state === 'loading'}
      className={cardClass(message.direction === 'outbound')}
      onClick={() => void download()}
    >
      <FileIcon aria-hidden className='size-8 shrink-0 opacity-80' />
      <CardText name={name} detail={extension(name)} />
      {state === 'loading' ? <SpinnerIcon className='size-5 shrink-0' /> : <Download aria-hidden className='size-5 shrink-0 opacity-80' />}
    </button>
  );
};

/** Fetched when the preview opens: mounted only then. */
const PdfPreview: FC<{ message: WhatsappMessage; name: string }> = ({ message, name }) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const { objectUrl, error } = useAuthenticatedObjectUrl(mediaUrl(workspace.id, message.id));

  if (error) return <p className={NOTE_CLASS}>{t('mediaUnavailable')}</p>;
  if (!objectUrl) return <SpinnerIcon className='mx-auto size-8' />;
  return (
    <>
      <iframe src={objectUrl} title={name} className='h-[75vh] w-[80vw] max-w-4xl rounded-md bg-white-100' />
      <DownloadLink url={objectUrl} name={name} />
    </>
  );
};

const PdfCard: FC<{ message: WhatsappMessage; name: string }> = ({ message, name }) => {
  const { t } = useTranslation('whatsappInbox');
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type='button' aria-label={`${t('openPreview')} ${name}`} className={cardClass(message.direction === 'outbound')} onClick={() => setOpen(true)}>
        <FileText aria-hidden className='size-8 shrink-0 opacity-80' />
        <CardText name={name} detail='PDF' />
      </button>
      {open ? (
        <PreviewModal title={name} onClose={() => setOpen(false)}>
          <PdfPreview message={message} name={name} />
        </PreviewModal>
      ) : null}
    </>
  );
};

const formatDuration = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/** A voice-note sized player; a format the browser cannot play (Safari and ogg, say) falls back to a download. */
const VoicePlayer: FC<{ src: string; name: string; outbound: boolean }> = ({ src, name, outbound }) => {
  const { t } = useTranslation('whatsappInbox');
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [unplayable, setUnplayable] = useState(false);

  if (unplayable) return <DownloadLink url={src} name={name} />;
  return (
    <div className='flex w-64 max-w-full items-center gap-3'>
      <audio
        ref={audio}
        src={src}
        preload='metadata'
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
        // some recordings report Infinity until fully read
        onDurationChange={(event) => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
        onError={() => setUnplayable(true)}
      />
      <button
        type='button'
        aria-label={playing ? t('pause') : t('play')}
        className={twMerge(
          'flex size-9 shrink-0 items-center justify-center rounded-full',
          outbound ? 'bg-white-100 text-brand-600' : 'bg-brand-600 text-white-100'
        )}
        onClick={() => (playing ? audio.current?.pause() : void audio.current?.play().catch(() => setUnplayable(true)))}
      >
        {playing ? <Pause className='size-4' /> : <Play className='size-4 translate-x-px' />}
      </button>
      <input
        type='range'
        min={0}
        max={duration}
        step={0.1}
        value={Math.min(time, duration)}
        aria-label={name}
        className={twMerge('h-1 min-w-0 flex-1 cursor-pointer', outbound ? 'accent-white-100' : 'accent-brand-600')}
        onChange={(event) => {
          if (audio.current) audio.current.currentTime = Number(event.target.value);
        }}
      />
      <span className='w-9 shrink-0 text-right text-xs tabular-nums opacity-75'>{formatDuration(playing || time > 0 ? time : duration)}</span>
    </div>
  );
};

/** Image, audio and video are shown in the bubble, so they are fetched as soon as the bubble renders. */
const InlineMedia: FC<{ message: WhatsappMessage; name: string }> = ({ message, name }) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const { objectUrl, error } = useAuthenticatedObjectUrl(mediaUrl(workspace.id, message.id));
  const [open, setOpen] = useState(false);

  if (error) return <p className={NOTE_CLASS}>{t('mediaUnavailable')}</p>;
  if (!objectUrl) return <p className={NOTE_CLASS}>{t('mediaLoading')}</p>;

  switch (mediaKind(message.media?.mime)) {
    case 'image':
      return (
        <>
          <button type='button' aria-label={`${t('openPreview')} ${name}`} className='block overflow-hidden rounded-lg' onClick={() => setOpen(true)}>
            <img src={objectUrl} alt={message.text || name} className='max-h-72 max-w-full' />
          </button>
          {open ? (
            <PreviewModal title={message.text || name} onClose={() => setOpen(false)}>
              <img src={objectUrl} alt={message.text || name} className='max-h-[80vh] max-w-[90vw] rounded-md object-contain' />
            </PreviewModal>
          ) : null}
        </>
      );
    case 'audio':
      return <VoicePlayer src={objectUrl} name={name} outbound={message.direction === 'outbound'} />;
    case 'video':
      return <video controls src={objectUrl} aria-label={name} className='max-h-72 max-w-full rounded-lg' />;
    default:
      return null; // 'pdf' and 'file' never get here: MessageMedia sends them to their cards
  }
};

export const MessageMedia: FC<{ message: WhatsappMessage }> = ({ message }) => {
  const { t } = useTranslation('whatsappInbox');
  const media = message.media;

  if (!media) return null;
  if (media.status === 'failed') return <p className={NOTE_CLASS}>{t('mediaUnavailable')}</p>;
  if (media.status !== 'stored') return <p className={NOTE_CLASS}>{t('mediaLoading')}</p>;

  const name = media.filename || message.id;
  switch (mediaKind(media.mime)) {
    case 'file':
      return <FileDownload message={message} name={name} />;
    case 'pdf':
      return <PdfCard message={message} name={name} />;
    default:
      return <InlineMedia message={message} name={name} />;
  }
};
